import { useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { Box } from '@mui/material'
import { useIntl } from 'react-intl'
import type { UiNode, UiText } from '@ory/client'

import { tokens } from '@configuration/theme'
import InlineMessage from '@base/InlineMessage/InlineMessage.component'
import TextField from '@base/TextField/TextField.component'
import ArrowRightIcon from '@icons/ArrowRight.icon'
import LockIcon from '@icons/Lock.icon'
import MailIcon from '@icons/Mail.icon'
import AuthError from '@components/AuthError/AuthError.component'
import AuthLoading from '@components/AuthLoading/AuthLoading.component'
import SplitAuthLayout from '@components/SplitAuthLayout/SplitAuthLayout.component'
import SubmitRow from '@components/SubmitRow/SubmitRow.component'
import { isLinkingMessage, translateKratosText } from '@i18n/kratos/kratos-i18n'
import { isSafePath, isSameOriginFormAction } from '@utils/url.utils'
import { findInput, findFieldError, getFlowMessages } from '@utils/kratos-nodes.utils'

import AlreadySignedInPanel from './components/AlreadySignedInPanel/AlreadySignedInPanel.component'
import SsoSection from './components/SsoSection/SsoSection.component'
import { useLoginFlow } from './hooks/useLoginFlow'
import { useSsoRoute, type SsoRoutePhase } from './hooks/useSsoRoute'

// Stable placeholder, not "this flow has no OIDC nodes" — used while the real
// flow is still loading (or on a terminal state where the SSO block never
// renders), see useSsoRoute's 'loading' vs 'flow' phase handling.
const EMPTY_OIDC_NODES: ReadonlyArray<UiNode> = []

const LoginPage = () => {
  const intl = useIntl()
  const location = useLocation()
  const navigate = useNavigate()
  const flowId = new URLSearchParams(location.search).get('flow')
  // Forwarded into /recovery so it can return the user to the original OAuth
  // challenge. isSafePath guards against a foreign origin sneaking in.
  const candidate = `${location.pathname}${location.search}`
  const safeReturnTo = isSafePath(candidate) ? candidate : '/login'
  const recoveryHref = `/recovery?return_to=${encodeURIComponent(safeReturnTo)}`
  // Carries the in-progress flow across to the dedicated SSO page — SsoSection
  // only renders once state.kind === 'flow', which requires flowId to already
  // be set (see useLoginFlow), so the fallback below never actually fires.
  const ssoHref = flowId ? `/login/sso?flow=${encodeURIComponent(flowId)}` : '/login/sso'

  const errorWelcome = intl.formatMessage({ id: 'login.error.welcome' })
  const state = useLoginFlow(flowId)

  // Hooks must run before the early returns below, so this derives from
  // `state` directly rather than the later `const { flow } = state` branch.
  const oidcNodes =
    state.kind === 'flow' ? state.flow.ui.nodes.filter((n) => n.group === 'oidc') : EMPTY_OIDC_NODES

  // OIDC linking ceremony: Kratos bounced the user back here after they
  // clicked an OIDC provider whose verified email matches an existing
  // password identity. Surface the prompt as a banner above the form and
  // hide the SSO block — re-submitting a provider would loop back here.
  // Single pass: first linking message wins (Kratos sends one at a time in
  // practice; multiple linking ids in one flow would render only the first).
  const flowMessages = state.kind === 'flow' ? getFlowMessages(state.flow) : []
  const linkingMessages: UiText[] = []
  const nonLinkingMessages: UiText[] = []
  for (const m of flowMessages) {
    if (isLinkingMessage(m)) {
      linkingMessages.push(m)
    } else {
      nonLinkingMessages.push(m)
    }
  }
  const linkingMessage = linkingMessages[0]

  // 'terminal' on the linking branch too — SsoSection never renders there
  // (`!linkingMessage &&` below), so the /hrd/config fetch would be wasted.
  let ssoPhase: SsoRoutePhase
  if (state.kind === 'loading') {
    ssoPhase = 'loading'
  } else if (state.kind !== 'flow' || linkingMessage) {
    ssoPhase = 'terminal'
  } else {
    ssoPhase = 'flow'
  }
  // The email-step UI lives on /login/sso — if a brand click can't resolve a
  // provider (e.g. /hrd/config unreachable), send the user there instead of
  // leaving the click a silent no-op. A remembered route may auto-submit on
  // that page — accepted: with HRD down the email lookup is dead too, so a
  // working remembered org route beats stranding the user on a dead form.
  const sso = useSsoRoute(oidcNodes, ssoPhase, { onNoPrimaryRoute: () => navigate(ssoHref) })

  // `.submit()`, never `.requestSubmit()`: the latter runs full HTML5
  // constraint validation, which blocks on the empty required
  // identifier/password fields — and there's no button here to carry
  // `formNoValidate` for a programmatic submit.
  const formRef = useRef<HTMLFormElement>(null)
  useEffect(() => {
    if (sso.pendingProvider) {
      formRef.current?.submit()
    }
  }, [sso.pendingProvider])

  if (state.kind === 'loading') {
    return <AuthLoading />
  }

  if (state.kind === 'session') {
    const returnTo = new URLSearchParams(location.search).get('return_to')
    return <AlreadySignedInPanel session={state.session} returnTo={returnTo} />
  }

  if (state.kind === 'error') {
    return (
      <AuthError
        welcome={errorWelcome}
        message={state.message || intl.formatMessage({ id: 'login.expired' })}
      />
    )
  }

  const { flow } = state
  const passwordGroup = flow.ui.nodes.filter((n) => n.group === 'password' || n.group === 'default')
  const csrfAttrs = findInput(passwordGroup, 'csrf_token')
  const identifierAttrs = findInput(passwordGroup, 'identifier')
  const passwordAttrs = findInput(passwordGroup, 'password', 'password')

  if (!csrfAttrs || !identifierAttrs || !passwordAttrs) {
    return (
      <AuthError
        welcome={errorWelcome}
        message={intl.formatMessage({ id: 'login.error.missingFields' })}
      />
    )
  }
  if (!isSameOriginFormAction(flow.ui.action)) {
    return (
      <AuthError
        welcome={errorWelcome}
        message={intl.formatMessage({ id: 'login.error.formActionUnexpectedOrigin' })}
      />
    )
  }

  const csrfValue = String(csrfAttrs.value ?? '')
  const identifierValue = String(identifierAttrs.value ?? '')
  const identifierError = findFieldError(passwordGroup, 'identifier', intl)
  const passwordError = findFieldError(passwordGroup, 'password', intl)

  return (
    <SplitAuthLayout
      heading={intl.formatMessage({ id: 'login.heading' })}
      subheading=''
      dataTestId='login-page'
    >
      {linkingMessage && (
        <InlineMessage variant='info' strong={true}>
          {translateKratosText(linkingMessage, intl)}
        </InlineMessage>
      )}
      {nonLinkingMessages.map((m) => (
        <InlineMessage key={m.id} variant={m.type === 'error' ? 'error' : 'info'}>
          {translateKratosText(m, intl)}
        </InlineMessage>
      ))}

      {!linkingMessage && (
        <SsoSection
          // A brand click always resolves through /hrd/config (remembered
          // routes only serve /login/sso), so gate on config alone.
          primaryDisabled={!sso.configReady}
          ssoHref={ssoHref}
          onSubmitPrimary={sso.submitPrimary}
        />
      )}

      <Box
        component='form'
        ref={formRef}
        action={flow.ui.action}
        method={flow.ui.method}
        sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <input type='hidden' name='csrf_token' value={csrfValue} />
        {sso.pendingProvider && <input type='hidden' name='provider' value={sso.pendingProvider} />}

        {/*
          TEMPORARY: type='text', not 'email'. Kratos identifiers are currently
          non-email usernames (e.g. katarina_hamann_edeka_sudwest), so the browser's
          native type='email' check rejects valid logins before submit. Revert to
          type='email' once we adopt the multi-company convention and identifiers
          become email addresses.
        */}

        <TextField
          name='identifier'
          type='text'
          icon={<MailIcon />}
          label={intl.formatMessage({ id: 'login.field.identifier.label' })}
          placeholder={intl.formatMessage({ id: 'login.field.identifier.placeholder' })}
          autoComplete='username'
          required={true}
          autoFocus={true}
          defaultValue={identifierValue}
          error={identifierError}
        />

        <TextField
          name='password'
          type='password'
          icon={<LockIcon />}
          label={intl.formatMessage({ id: 'login.field.password.label' })}
          hint={
            <Box
              component={Link}
              to={recoveryHref}
              sx={{
                fontSize: 12,
                color: tokens.versatileViolet,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {intl.formatMessage({ id: 'login.forgotPassword' })}
            </Box>
          }
          placeholder={intl.formatMessage({ id: 'login.field.password.placeholder' })}
          autoComplete='current-password'
          required={true}
          error={passwordError}
        />

        <SubmitRow
          submitName='method'
          submitValue='password'
          submitLabel={intl.formatMessage({ id: 'login.submit' })}
          submitEndIcon={<ArrowRightIcon />}
        />
      </Box>
    </SplitAuthLayout>
  )
}

export default LoginPage
